import {
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  NotInterested as BlockIcon,
} from "@mui/icons-material";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { deleteEntry, markEntryInvalid } from "../features/admin/adminSlice";
import { useAccessControl } from "../hooks/useAccessControl";

const AdminEntryActions = ({ entry, onActionComplete }) => {
  const { isAdmin } = useAccessControl();
  const dispatch = useDispatch();
  const { isLoading, isError, message } = useSelector((state) => state.admin);

  const [anchorEl, setAnchorEl] = useState(null);
  const [dialog, setDialog] = useState({ open: false, type: null });
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  if (!isAdmin) {
    return null;
  }

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openDialog = (type) => {
    setDialog({ open: true, type });
    handleMenuClose();
    setError("");
    setReason("");
  };

  const closeDialog = () => {
    setDialog({ open: false, type: null });
    setError("");
    setReason("");
  };

  const handleDelete = async () => {
    const result = await dispatch(deleteEntry(entry.id));
    if (result.type === deleteEntry.fulfilled.type) {
      closeDialog();
      onActionComplete?.("deleted");
    } else {
      setError(result.payload || "Failed to delete entry");
    }
  };

  const handleMarkInvalid = async () => {
    const result = await dispatch(
      markEntryInvalid({ entryId: entry.id, reason })
    );
    if (result.type === markEntryInvalid.fulfilled.type) {
      closeDialog();
      onActionComplete?.("marked_invalid");
    } else {
      setError(result.payload || "Failed to mark entry as invalid");
    }
  };

  const getDialogConfig = () => {
    switch (dialog.type) {
      case "delete":
        return {
          title: "Delete Entry",
          content:
            "Are you sure you want to delete this entry? This action cannot be undone.",
          action: handleDelete,
          actionLabel: "Delete",
          color: "error",
        };
      case "invalid":
        return {
          title: "Mark Entry as Invalid",
          content: "Enter a reason for marking this entry as invalid:",
          action: handleMarkInvalid,
          actionLabel: "Mark Invalid",
          color: "warning",
          showReasonField: true,
        };
      case "edit":
        return {
          title: "Edit Entry",
          content: "Edit functionality coming soon.",
          action: null,
          actionLabel: "Edit",
          color: "primary",
        };
      default:
        return null;
    }
  };

  const config = getDialogConfig();

  return (
    <>
      <IconButton size="small" onClick={handleMenuOpen} title="Admin Actions">
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => openDialog("edit")}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => openDialog("invalid")}>
          <BlockIcon fontSize="small" sx={{ mr: 1 }} />
          Mark Invalid
        </MenuItem>
        <MenuItem
          onClick={() => openDialog("delete")}
          sx={{ color: "error.main" }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {config && (
        <Dialog
          open={dialog.open}
          onClose={closeDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{config.title}</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {config.content}
            {config.showReasonField && (
              <TextField
                autoFocus
                fullWidth
                label="Reason"
                multiline
                rows={3}
                margin="normal"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this entry invalid?"
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>Cancel</Button>
            {config.action && (
              <Button
                onClick={config.action}
                variant="contained"
                color={config.color}
                disabled={isLoading}
              >
                {isLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  config.actionLabel
                )}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default AdminEntryActions;
